import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, Plus, Search } from 'lucide-react';
import { couponsAPI } from '../../lib/api';
import { t } from '../../lib/i18n';
import { btnDanger, btnGhost, btnPrimary, EmptyState, Field, inputCls, Modal, panelCls } from '../../components/ui';
import { nyTime } from '../../lib/nyTime';

// s149 — single-use coupons for the KMS reader subscription. The discount is a
// fixed USD amount or a percentage, valid for ONE plan, and applies to every
// charge of the subscription. Applied by the reader at the KMS paywall.

const STATUS_STYLE = {
  available: 'text-[var(--mms-success)] border-[var(--mms-success)]',
  reserved: 'text-[var(--mms-warning)] border-[var(--mms-warning)]',
  used: 'text-mms-muted border-mms-border',
};
const STATUS_LABEL = { available: 'Available', reserved: 'In checkout', used: 'Used' };

const discountLabel = (c) => (c.discount_type === 'amount' ? `$${Number(c.value).toLocaleString()}` : `${c.value}%`);
const money = (v) => (v === undefined || v === null ? '' : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`);

function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex rounded-md border border-mms-border p-0.5" role="radiogroup">
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={`rounded px-3 py-1 text-sm transition-colors duration-150 active:scale-[0.97] ${
            value === o.value ? 'bg-mms-accent text-mms-accent-ink font-semibold' : 'text-mms-muted hover:text-mms-text'}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function CreateModal({ onClose, onDone }) {
  const [kind, setKind] = useState('percent');
  const [value, setValue] = useState('');
  const [plan, setPlan] = useState('monthly');
  const [code, setCode] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await couponsAPI.create({ discount_type: kind, value: Number(value), plan, code, note });
      toast.success(`${t('Coupon created')}: ${data.code}`);
      onDone();
    } catch (err) {
      toast.error(t(err.response?.data?.detail || 'Could not create the coupon'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={t('New coupon')} onClose={onClose}>
      <div className="space-y-4">
        <Field label={t('Discount')}>
          <div className="flex items-center gap-2">
            <Segmented value={kind} onChange={setKind}
              options={[{ value: 'percent', label: '%' }, { value: 'amount', label: 'USD $' }]} />
            <input className={inputCls} type="number" min="0" step="0.01" value={value} autoFocus
              placeholder={kind === 'percent' ? '50' : '25'} onChange={(e) => setValue(e.target.value)} />
          </div>
        </Field>
        <Field label={t('Plan')} hint={t('The coupon only works on this plan (KMS or membership). The discount applies to every charge.')}>
          <Segmented value={plan} onChange={setPlan}
            options={[{ value: 'monthly', label: t('Monthly') }, { value: 'annual', label: t('Annual') }]} />
        </Field>
        <Field label={t('Code (optional)')} hint={t('Leave empty to generate one.')}>
          <input className={`${inputCls} uppercase`} value={code} onChange={(e) => setCode(e.target.value)} />
        </Field>
        <Field label={t('Note (optional)')}>
          <input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder={t('Who is it for?')} />
        </Field>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className={btnGhost}>{t('Cancel')}</button>
        <button onClick={save} disabled={busy || !(Number(value) > 0)} className={btnPrimary}>
          {busy ? t('Working…') : t('Create coupon')}
        </button>
      </div>
    </Modal>
  );
}

export default function CouponsPage() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    couponsAPI.list({ status, search })
      .then(({ data }) => setData(data))
      .catch(() => toast.error(t('Could not load coupons')));
  }, [status, search]);
  useEffect(() => {
    const h = setTimeout(load, 250);
    return () => clearTimeout(h);
  }, [load]);

  const copy = (code) => {
    navigator.clipboard?.writeText(code).then(() => toast.success(t('Copied')));
  };
  const release = async (c) => {
    try { await couponsAPI.release(c.id); load(); } catch { toast.error(t('Action failed')); }
  };
  const remove = async (c) => {
    if (!window.confirm(`${t('Delete coupon')} ${c.code}?`)) return;
    try { await couponsAPI.remove(c.id); load(); } catch (err) { toast.error(t(err.response?.data?.detail || 'Action failed')); }
  };

  const s = data?.summary || {};
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">{t('Coupons')}</h1>
          <p className="text-sm text-mms-muted">{t('Single-use discounts for the KMS subscription and the membership.')}</p>
        </div>
        <button onClick={() => setCreating(true)} className={`${btnPrimary} active:scale-[0.97] transition-transform duration-150`}>
          <Plus size={15} /> {t('New coupon')}
        </button>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        {[['', 'All', s.total], ['available', 'Available', s.available], ['reserved', 'In checkout', s.reserved], ['used', 'Used', s.used]].map(([v, l, n]) => (
          <button key={v || 'all'} onClick={() => setStatus(v)}
            className={`rounded-full border px-3 py-1 text-sm transition-colors duration-150 ${
              status === v ? 'border-[var(--mms-focus-border)] text-mms-text' : 'border-mms-border text-mms-muted hover:text-mms-text'}`}>
            {t(l)} <span className="mms-num text-mms-faint">{n ?? 0}</span>
          </button>
        ))}
        <div className="relative ml-auto w-full sm:w-64">
          <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-mms-faint" />
          <input className={`${inputCls} pl-8`} value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={t('Code, name, email, member…')} />
        </div>
      </div>

      {data === null ? (
        <p className="text-sm text-mms-muted">{t('Loading…')}</p>
      ) : data.coupons.length === 0 ? (
        <EmptyState>{t('No coupons here yet.')}</EmptyState>
      ) : (
        <div className={`${panelCls} overflow-x-auto`}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-mms-border text-left text-xs text-mms-faint">
                <th className="px-4 py-2.5 font-medium">{t('Code')}</th>
                <th className="px-4 py-2.5 font-medium text-right">{t('Discount')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Plan')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Status')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Used by')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Used in')}</th>
                <th className="px-4 py-2.5 font-medium text-right">{t('Price → paid')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Note')}</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {data.coupons.map((c) => (
                <tr key={c.id} className="border-b border-mms-border/60 last:border-0">
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <button onClick={() => copy(c.code)} title={t('Copy')}
                      className="inline-flex items-center gap-1.5 font-mono text-[13px] hover:text-[var(--mms-focus-border)]">
                      {c.code} <Copy size={12} className="text-mms-faint" />
                    </button>
                  </td>
                  <td className="px-4 py-2.5 text-right mms-num">{discountLabel(c)}</td>
                  <td className="px-4 py-2.5">{t(c.plan === 'annual' ? 'Annual' : 'Monthly')}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-block rounded-full border px-2 py-0.5 text-xs ${STATUS_STYLE[c.status]}`}>{t(STATUS_LABEL[c.status])}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    {c.member ? (
                      <div>
                        <div>{[c.member.first_name, c.member.last_name].filter(Boolean).join(' ') || c.member.email}
                          {c.member.member_code ? <span className="ml-1.5 text-xs text-mms-muted">{c.member.member_code}</span> : null}</div>
                        <div className="text-xs text-mms-faint">{nyTime(c.used_at)}</div>
                      </div>
                    ) : c.checkout_member ? (
                      <span className="text-xs text-mms-faint">{t('Checkout opened by')} {c.checkout_member.email}</span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-mms-muted whitespace-nowrap">
                    {c.status === 'used' ? t(c.used_for === 'cms_membership' ? 'Membership' : 'KMS') : ''}
                  </td>
                  <td className="px-4 py-2.5 text-right mms-num whitespace-nowrap">
                    {c.status === 'used' ? `${money(c.list_price)} → ${money(c.amount_paid)}` : ''}
                  </td>
                  <td className="px-4 py-2.5 max-w-[16rem] truncate text-mms-muted" title={c.note}>{c.note}</td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    {c.status === 'reserved' && (
                      <button onClick={() => release(c)} className={`${btnGhost} mr-1.5`}>{t('Release')}</button>
                    )}
                    {c.status !== 'used' && (
                      <button onClick={() => remove(c)} className={btnDanger}>{t('Delete')}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {creating && <CreateModal onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />}
    </div>
  );
}
